import { useCallback } from 'react';

import { useSnackbarNotifications as useSnackbarNotificationsContext } from '@/context/SnackbarNotificationsContext';

import type { SnackbarKey } from 'notistack';

// Re-export the new context-based implementation
export { SnackbarNotificationsProvider } from '@/context/SnackbarNotificationsContext';
export type { SnackbarMessage } from '@/context/SnackbarNotificationsContext';
export { useSnackbarNotificationsContext };

/**
 * Legacy hook for backward compatibility.
 * Uses snackIdRef to track a single notification at a time.
 *
 * For new code, consider using useSnackbarNotificationsContext
 * with requestKey pattern to manage multiple concurrent notifications.
 */
const useSnackbarNotifications = () => {
  const contextFunctions = useSnackbarNotificationsContext();

  const showErrorSnackbar = useCallback(
    // eslint-disable-next-line sonarjs/function-return-type
    (message: string, autoHideDuration?: number, key?: SnackbarKey): SnackbarKey => {
      // Use provided key or generate a default one
      const requestKey = key?.toString() || 'default';
      return contextFunctions.showErrorSnackbar(requestKey, message, autoHideDuration);
    },
    [contextFunctions]
  );

  const showLoadingSnackbar = useCallback(
    // eslint-disable-next-line sonarjs/function-return-type
    (message: string, key?: SnackbarKey): SnackbarKey => {
      const requestKey = key?.toString() || 'default';
      return contextFunctions.showLoadingSnackbar(requestKey, message);
    },
    [contextFunctions]
  );

  const showSuccessSnackbar = useCallback(
    // eslint-disable-next-line sonarjs/function-return-type
    (message: string, key?: SnackbarKey): SnackbarKey => {
      const requestKey = key?.toString() || 'default';
      return contextFunctions.showSuccessSnackbar(requestKey, message);
    },
    [contextFunctions]
  );

  return {
    showErrorSnackbar,
    showLoadingSnackbar,
    showSuccessSnackbar
  };
};

export default useSnackbarNotifications;
